import { createSlice } from "@reduxjs/toolkit";
import {
  getTemporaryMsg,
  createOrUpdateTemporaryMsg,
  getTemporaryMsgMediaFile,
  getResetTemporaryMsg,
} from "../services/temporaryMsg";

interface TemporaryMsgState {
  temporaryMsgStatus: string;
  temporaryMsgMediaFileStatus: string;
  isTMPending: boolean;
  isTMSave: boolean;
  isTMDelete: boolean;
  isTMError: boolean;
  temporaryMsgData: Record<string, unknown>[];
  temporaryMsgMediaFileInfo: unknown | null;
  closureOriginalData?: Record<string, unknown>[];
}

const initialState: TemporaryMsgState = {
  temporaryMsgStatus: "idle",
  temporaryMsgMediaFileStatus: "idle",
  isTMPending: false,
  isTMSave: false,
  isTMDelete: false,
  isTMError: false,
  temporaryMsgData: [],
  temporaryMsgMediaFileInfo: null,
};

const processPayload = (
  payload: Record<string, unknown>,
): Record<string, unknown> => {
  const clonedPayload = { ...payload };
  delete clonedPayload["__typename"];
  delete clonedPayload["__operation"];
  return clonedPayload;
};

const processMessages = (processedPayload: Record<string, unknown>) => {
  try {
    const raw = processedPayload.messages;
    const parsedMessages =
      typeof raw === "string"
        ? (JSON.parse(raw) as Record<string, unknown> | null)
        : (raw as Record<string, unknown> | null);
    return {
      message_en: parsedMessages?.en ?? null,
      message_fr: parsedMessages?.fr ?? null,
      message_sp: parsedMessages?.sp ?? null,
      message_zh: parsedMessages?.zh ?? null,
      message_zhTW: parsedMessages?.["zh-TW"] ?? null,
    };
  } catch {
    return {
      message_en: null,
      message_fr: null,
      message_sp: null,
      message_zh: null,
      message_zhTW: null,
    };
  }
};
const temporaryMsgSlice = createSlice({
  name: "temporaryMsg",
  initialState,
  reducers: {
    createTMData: (
      state,
      { payload }: { payload: Record<string, unknown> },
    ) => {
      const processedPayload = processPayload(payload);
      state.temporaryMsgData.push({
        ...processedPayload,
        ...processMessages(processedPayload),
        highLight: true,
      });
    },

    updateTMData: (
      state,
      { payload }: { payload: Record<string, unknown> },
    ) => {
      const processedPayload = processPayload(payload);
      const messages = processMessages(processedPayload);
      state.temporaryMsgData = state.temporaryMsgData.map((item) => {
        if (item.sequenceNumber === processedPayload.sequenceNumber) {
          return { ...processedPayload, ...messages, highLight: true };
        } else {
          return { ...item, highLight: false };
        }
      });
    },

    resetHighlight: (
      state,
      { payload }: { payload: Record<string, unknown>[] },
    ) => {
      state.temporaryMsgData = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTemporaryMsg.pending, (state) => {
        state.temporaryMsgStatus = "pending";
        state.isTMPending = false;
        state.isTMSave = false;
        state.isTMDelete = false;
        state.isTMError = false;
        state.temporaryMsgData = [];
      })
      .addCase(getTemporaryMsg.fulfilled, (state, action) => {
        const payload = action.payload as {
          data: { data: Record<string, unknown>[] };
        };
        state.temporaryMsgStatus = "success";
        state.isTMPending = false;
        state.temporaryMsgData = payload.data.data;
      })
      .addCase(getTemporaryMsg.rejected, (state) => {
        state.temporaryMsgStatus = "rejected";
        state.isTMPending = false;
        state.isTMError = true;
        state.temporaryMsgData = [];
      })
      .addCase(getTemporaryMsgMediaFile.pending, (state) => {
        state.temporaryMsgMediaFileStatus = "pending";
        state.temporaryMsgMediaFileInfo = null;
      })
      .addCase(getTemporaryMsgMediaFile.fulfilled, (state, action) => {
        state.temporaryMsgMediaFileStatus = "success";
        state.temporaryMsgMediaFileInfo = action.payload as unknown;
      })
      .addCase(getTemporaryMsgMediaFile.rejected, (state) => {
        state.temporaryMsgMediaFileStatus = "rejected";
        state.temporaryMsgMediaFileInfo = null;
      })
      .addCase(createOrUpdateTemporaryMsg.pending, (state) => {
        state.isTMPending = true;
        state.isTMSave = false;
        state.isTMDelete = false;
        state.isTMError = false;
      })
      .addCase(createOrUpdateTemporaryMsg.fulfilled, (state, action) => {
        const payload = action.payload as {
          data: { data: Record<string, unknown>[] };
        };
        state.temporaryMsgStatus = "created";
        state.closureOriginalData = payload.data.data;
        state.temporaryMsgData = payload.data.data;
        state.isTMPending = false;
        state.isTMSave = true;
        state.isTMDelete = false;
        state.isTMError = false;
      })
      .addCase(createOrUpdateTemporaryMsg.rejected, (state) => {
        state.temporaryMsgStatus = "rejected";
        state.isTMPending = false;
        state.isTMSave = false;
        state.isTMDelete = false;
        state.isTMError = true;
      })
      .addCase(getResetTemporaryMsg, (state) => {
        state.temporaryMsgStatus = "idle";
        state.isTMPending = false;
        state.isTMSave = false;
        state.isTMDelete = false;
        state.isTMError = false;
      });
  },
});
export const { updateTMData, createTMData, resetHighlight } =
  temporaryMsgSlice.actions;
export default temporaryMsgSlice.reducer;
